import { motion } from "framer-motion";
import { WHY } from "@/lib/content";
import { SectionHead, Reveal } from "@/components/Kinetic";

const EM = "#087F5B";
const DOT = "#8FE3C2";
const INK = "#17211D";
const ST = "#D5DDD8";
const t = (d = 0) => ({ duration: 0.9, delay: d, ease: [0.22, 1, 0.36, 1] });

const Glyph = ({ children }) => (
  <motion.svg viewBox="0 0 200 110" className="h-auto w-full max-w-[260px]" aria-hidden="true" initial="hidden" whileInView="show" viewport={{ once: false, amount: 0.5 }}>
    {children}
  </motion.svg>
);

const GClean = () => (
  <Glyph>
    <path d="M20 10 V100 M100 10 V100 M180 10 V100" stroke={ST} strokeDasharray="3 5" />
    {[[20, 18, 80, 30], [20, 56, 50, 16], [20, 80, 64, 16], [106, 18, 74, 78]].map(([x, y, w, h], i) => (
      <motion.rect key={i} x={x} y={y} width={w} height={h} rx="6" fill={i === 3 ? EM : INK}
        variants={{ hidden: { x: i % 2 ? 14 : -14, opacity: 0 }, show: { x: 0, opacity: 1, transition: t(0.1 * i) } }} />
    ))}
  </Glyph>
);

const GResponsive = () => (
  <Glyph>
    {[[10, 14, 118, 82, 0], [118, 30, 52, 70, 0.15], [160, 48, 30, 52, 0.3]].map(([x, y, w, h, d], i) => (
      <motion.g key={i} variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: t(d) } }}>
        <rect x={x} y={y} width={w} height={h} rx="8" fill="#FFFFFF" stroke={i === 2 ? EM : INK} strokeWidth="2.5" />
        <rect x={x + 8} y={y + 10} width={w - 16} height="8" rx="3" fill={i === 2 ? EM : INK} />
      </motion.g>
    ))}
  </Glyph>
);

const GSmooth = () => (
  <Glyph>
    <motion.path d="M12 88 C60 88 60 22 100 22 S140 88 188 40" fill="none" stroke={EM} strokeWidth="4" strokeLinecap="round"
      variants={{ hidden: { pathLength: 0 }, show: { pathLength: 1, transition: { duration: 1.4, ease: "easeInOut" } } }} />
    <circle cx="12" cy="88" r="6" fill={INK} />
    <motion.circle cx="188" cy="40" r="8" fill={DOT} stroke={EM} strokeWidth="2" variants={{ hidden: { scale: 0 }, show: { scale: 1, transition: t(1.2) } }} style={{ transformBox: "fill-box", transformOrigin: "center" }} />
  </Glyph>
);

const GDetail = () => (
  <Glyph>
    {Array.from({ length: 24 }).map((_, i) => (
      <rect key={i} x={20 + (i % 8) * 14} y={20 + Math.floor(i / 8) * 14} width="10" height="10" rx="2" fill={i === 13 ? EM : ST} />
    ))}
    <motion.g variants={{ hidden: { x: 40, y: 20, opacity: 0 }, show: { x: 0, y: 0, opacity: 1, transition: t(0.3) } }}>
      <circle cx="117" cy="47" r="22" fill="none" stroke={INK} strokeWidth="4" />
      <path d="M133 63 L154 84" stroke={INK} strokeWidth="7" strokeLinecap="round" />
    </motion.g>
  </Glyph>
);

const GNeeds = () => (
  <Glyph>
    <rect x="30" y="64" width="140" height="34" rx="8" fill={ST} />
    <motion.rect x="30" y="24" width="64" height="34" rx="8" fill={INK} variants={{ hidden: { y: -20, opacity: 0 }, show: { y: 0, opacity: 1, transition: t(0.1) } }} />
    <motion.rect x="100" y="24" width="70" height="34" rx="8" fill={EM} variants={{ hidden: { y: -30, opacity: 0 }, show: { y: 0, opacity: 1, transition: t(0.35) } }} />
  </Glyph>
);

const GLYPHS = [GClean, GResponsive, GSmooth, GDetail, GNeeds];
const SPANS = ["md:col-span-3", "md:col-span-3", "md:col-span-2", "md:col-span-2", "md:col-span-2"];

export default function WhyFaiz() {
  return (
    <section id="why" data-testid="why-section" className="relative overflow-hidden bg-soft py-24 lg:py-36">
      <div className="wrap">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-end">
          <SectionHead eyebrow="WHY FAIZ" title="WHY FAIZ." hl={[1]} testId="why-heading" className="lg:col-span-7" />
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted lg:col-span-5 lg:text-right">05 principles</span>
        </div>
        <div className="mt-14 grid gap-4 md:grid-cols-6">
          {WHY.map((w, i) => {
            const G = GLYPHS[i];
            return (
              <Reveal key={w} delay={0.06 * i} className={SPANS[i]}>
                <div data-testid={`why-faiz-item-${i + 1}`} className="group flex h-full flex-col justify-between gap-10 rounded-[28px] bg-white p-7 shadow-soft ring-1 ring-line transition-[box-shadow,transform] duration-500 hover:-translate-y-1 hover:ring-emerald sm:p-9">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-emerald">0{i + 1}</span>
                    <span className="h-2 w-2 rounded-full bg-line transition-colors duration-500 group-hover:bg-emerald" />
                  </div>
                  <G />
                  <p className="display text-2xl leading-[1.05] text-ink sm:text-[28px]">{w}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
